import type { BBox, Kks } from "@shared/types/domain";
import type React from "react";
import { MM_TO_PTS } from "../utils/kks";

const SPOF_EXPAND_PX = 4;

const RESIZE_HANDLES: Array<{
  type: string;
  cursor: string;
  style: React.CSSProperties;
}> = [
  {
    type: "n",
    cursor: "n-resize",
    style: {
      top: -4,
      left: "50%",
      transform: "translateX(-50%)",
      width: 20,
      height: 8,
      borderTopWidth: 2,
      borderTopStyle: "solid",
    },
  },
  {
    type: "s",
    cursor: "s-resize",
    style: {
      bottom: -4,
      left: "50%",
      transform: "translateX(-50%)",
      width: 20,
      height: 8,
      borderBottomWidth: 2,
      borderBottomStyle: "solid",
    },
  },
  {
    type: "e",
    cursor: "e-resize",
    style: {
      right: -4,
      top: "50%",
      transform: "translateY(-50%)",
      width: 8,
      height: 20,
      borderRightWidth: 2,
      borderRightStyle: "solid",
    },
  },
  {
    type: "w",
    cursor: "w-resize",
    style: {
      left: -4,
      top: "50%",
      transform: "translateY(-50%)",
      width: 8,
      height: 20,
      borderLeftWidth: 2,
      borderLeftStyle: "solid",
    },
  },
  {
    type: "ne",
    cursor: "ne-resize",
    style: {
      top: -4,
      right: -4,
      width: 10,
      height: 10,
      borderTopWidth: 2,
      borderTopStyle: "solid",
      borderRightWidth: 2,
      borderRightStyle: "solid",
    },
  },
  {
    type: "nw",
    cursor: "nw-resize",
    style: {
      top: -4,
      left: -4,
      width: 10,
      height: 10,
      borderTopWidth: 2,
      borderTopStyle: "solid",
      borderLeftWidth: 2,
      borderLeftStyle: "solid",
    },
  },
  {
    type: "se",
    cursor: "se-resize",
    style: {
      bottom: -4,
      right: -4,
      width: 10,
      height: 10,
      borderBottomWidth: 2,
      borderBottomStyle: "solid",
      borderRightWidth: 2,
      borderRightStyle: "solid",
    },
  },
  {
    type: "sw",
    cursor: "sw-resize",
    style: {
      bottom: -4,
      left: -4,
      width: 10,
      height: 10,
      borderBottomWidth: 2,
      borderBottomStyle: "solid",
      borderLeftWidth: 2,
      borderLeftStyle: "solid",
    },
  },
];

interface Props {
  kks: Kks[];
  selectedIds: Set<string>;
  resizingBbox: { id: string; bbox: BBox } | null;
  drawing: boolean;
  drawRect: {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null;
  ctrlSelectStart: { x: number; y: number } | null;
  ctrlSelectCurrent: { x: number; y: number } | null;
  pageScale: number;
  pidLocked: boolean;
  isMultiSelect: boolean;
  onSelect: (id: string, ctrl: boolean) => void;
  onResizeStart: (
    e: React.MouseEvent,
    kksId: string,
    handleType: string,
    bbox: BBox,
  ) => void;
  onMouseDown: React.MouseEventHandler<HTMLDivElement>;
  onMouseMove: React.MouseEventHandler<HTMLDivElement>;
  onMouseUp: () => void;
}

export function KksPdfOverlay({
  kks,
  selectedIds,
  resizingBbox,
  drawing,
  drawRect,
  ctrlSelectStart,
  ctrlSelectCurrent,
  pageScale,
  pidLocked,
  isMultiSelect,
  onSelect,
  onResizeStart,
  onMouseDown,
  onMouseMove,
  onMouseUp,
}: Props) {
  const bboxToStyle = (bbox: BBox) => ({
    left: bbox.x0 * MM_TO_PTS * pageScale,
    top: bbox.y0 * MM_TO_PTS * pageScale,
    width: (bbox.x1 - bbox.x0) * MM_TO_PTS * pageScale,
    height: (bbox.y1 - bbox.y0) * MM_TO_PTS * pageScale,
  });

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: drawing ? 20 : 10,
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      <style>{`
        [data-kks-handle] { border-color: rgba(25,118,210,.3); transition: border-color .15s; }
        [data-kks-handle]:hover { border-color: rgba(25,118,210,.85); }
      `}</style>
      {kks.map((item) => {
        if (!item.bbox) return null;
        const displayBbox =
          resizingBbox?.id === item.id ? resizingBbox.bbox : item.bbox;
        const baseStyle = bboxToStyle(displayBbox);
        const expand = item.spof ? SPOF_EXPAND_PX : 0;
        const style = {
          left: baseStyle.left - expand,
          top: baseStyle.top - expand,
          width: baseStyle.width + expand * 2,
          height: baseStyle.height + expand * 2,
        };
        const isSelected = selectedIds.has(item.id);
        const borderStyle = item.spof
          ? "3px solid #b71c1c"
          : "1.5px solid #546e7a";
        const bgColor = item.spof_approved
          ? "rgba(27, 94, 32, 0.35)"
          : item.verified
            ? "rgba(13, 71, 161, 0.30)"
            : "rgba(69, 90, 100, 0.20)";

        return (
          <div
            key={item.id}
            style={{
              position: "absolute",
              ...style,
              border: borderStyle,
              backgroundColor: bgColor,
              cursor: drawing ? "crosshair" : "pointer",
              zIndex: isSelected ? 10 : 1,
              boxSizing: "content-box",
              margin: item.spof ? "-3px" : "-1.5px",
              overflow: "visible",
              transition:
                "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
              ...(isSelected && {
                boxShadow: "0 0 0 2px #fff, 0 0 0 4px #1976d2",
              }),
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!drawing) onSelect(item.id, e.ctrlKey || e.metaKey);
            }}
          >
            {item.stamp_on_pdf &&
              (() => {
                // Match backend: fontsize = min(height, width / text_width_at_1pt)
                // with 10% inset margin on each side
                const charWidthRatio = 0.62;
                const insetW = style.width * 0.8; // 10% margin each side
                const insetH = style.height * 0.8;
                const textLen = item.text.length * charWidthRatio;
                const fsFromHeight = insetH;
                const fsFromWidth =
                  textLen > 0 ? insetW / textLen : fsFromHeight;
                const fontSize = Math.min(fsFromHeight, fsFromWidth);
                return (
                  <div
                    style={{
                      position: "absolute",
                      inset: "10%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize,
                      color: "#000000",
                      fontFamily: "Helvetica, Arial, sans-serif",
                      pointerEvents: "none",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.text}
                  </div>
                );
              })()}
            {isSelected &&
              !drawing &&
              !pidLocked &&
              !isMultiSelect &&
              RESIZE_HANDLES.map(({ type, cursor, style: hStyle }) => (
                <div
                  key={type}
                  data-kks-handle=""
                  style={{
                    position: "absolute",
                    cursor,
                    zIndex: 20,
                    boxSizing: "border-box",
                    background: "transparent",
                    ...hStyle,
                  }}
                  onMouseDown={(e) =>
                    onResizeStart(e, item.id, type, item.bbox!)
                  }
                  onClick={(e) => e.stopPropagation()}
                />
              ))}
          </div>
        );
      })}
      {drawRect && (
        <div
          style={{
            position: "absolute",
            left: drawRect.left,
            top: drawRect.top,
            width: drawRect.width,
            height: drawRect.height,
            border: "2px dashed #1976d2",
            backgroundColor: "rgba(25, 118, 210, 0.1)",
            pointerEvents: "none",
          }}
        />
      )}
      {ctrlSelectStart &&
        ctrlSelectCurrent &&
        (() => {
          const x0 = Math.min(ctrlSelectStart.x, ctrlSelectCurrent.x);
          const y0 = Math.min(ctrlSelectStart.y, ctrlSelectCurrent.y);
          const w = Math.abs(ctrlSelectCurrent.x - ctrlSelectStart.x);
          const h = Math.abs(ctrlSelectCurrent.y - ctrlSelectStart.y);
          return (
            <div
              style={{
                position: "absolute",
                left: x0,
                top: y0,
                width: w,
                height: h,
                border: "2px dashed #7b1fa2",
                backgroundColor: "rgba(123, 31, 162, 0.08)",
                pointerEvents: "none",
                zIndex: 25,
              }}
            />
          );
        })()}
    </div>
  );
}
